import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Maximize, Calendar, Clock } from 'lucide-react';

const PropertyCard = ({ property }) => {
  const {
    property_id,
    title,
    city,
    area,
    sqm,
    rooms,
    auction_start_price,
    auction_status,
    auction_date,
    auction_time,
    images = []
  } = property;

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = () => {
    const baseClasses = "inline-flex items-center px-3 py-1 text-[11px] font-semibold rounded-full";
    switch (auction_status) {
      case 'active':
        return <span className={`${baseClasses} bg-green-50 text-green-700`}>Gara in Corso</span>;
      case 'scheduled':
        return <span className={`${baseClasses} bg-blue-50 text-blue-700`}>Gara Programmata</span>;
      case 'published':
        return <span className={`${baseClasses} bg-amber-50 text-amber-700`}>In Vendita</span>;
      case 'closed':
        return <span className={`${baseClasses} bg-gray-100 text-gray-600`}>Venduto</span>;
      default:
        return null;
    }
  };

  const defaultImage = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';

  // Generate SEO-friendly alt text
  const altText = `${title || 'Immobile'} in vendita a ${city || 'Italia'}${area ? `, zona ${area}` : ''} - ${sqm ? `${sqm} mq` : ''} ${rooms ? `${rooms} locali` : ''}`.trim();

  return (
    <Link to={`/immobili/${property_id}`} data-testid={`property-card-${property_id}`}>
      <article className="property-card group" itemScope itemType="https://schema.org/RealEstateListing">
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={images[0] || defaultImage}
            alt={altText}
            title={`${title} - ${city}`}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-4 left-4">
            {getStatusBadge()}
          </div>
          {auction_status === 'active' && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-bold text-white bg-red-500 rounded-full animate-subtle-pulse">
                <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5"></span>
                LIVE
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Location */}
          <div className="flex items-center text-[13px] text-[#86868B] mb-2">
            <MapPin className="h-3.5 w-3.5 mr-1 flex-shrink-0" strokeWidth={1.5} />
            <span className="line-clamp-1">{city}{area ? ` • ${area}` : ''}</span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-[#1D1D1F] line-clamp-1 mb-3 tracking-tight">
            {title}
          </h3>

          {/* Features */}
          <div className="flex items-center gap-4 text-[13px] text-[#86868B] mb-4">
            <div className="flex items-center">
              <Maximize className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              <span>{sqm} m²</span>
            </div>
            {rooms && (
              <div className="flex items-center">
                <Bed className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                <span>{rooms} locali</span>
              </div>
            )}
          </div>

          {/* Auction Date */}
          {auction_date && (
            <div className="flex items-center text-[13px] text-[#0A2540] mb-4">
              <Calendar className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              <span>{formatDate(auction_date)}</span>
              {auction_time && (
                <>
                  <span className="mx-2 text-[#E5E5EA]">|</span>
                  <Clock className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                  <span>{auction_time}</span>
                </>
              )}
            </div>
          )}

          {/* Price */}
          <div className="pt-4 border-t border-black/5">
            <div className="flex items-baseline justify-between">
              <span className="text-[12px] text-[#86868B]">Prezzo base</span>
              <span className="text-xl font-bold text-[#1D1D1F] tabular-nums tracking-tight" itemProp="price">
                {formatPrice(auction_start_price)}
              </span>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default PropertyCard;
